import {
  BulbOutlined,
  CloseOutlined,
  DislikeOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  LikeOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  PauseCircleOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
  SmileOutlined,
  StarOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Button, Divider, Dropdown, Flex, Input, Popconfirm, Rate, Typography } from "antd";
import dayjs from "dayjs";
import markdownit from "markdown-it";
import { useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { Bubble } from "src/components/Bubble/Bubble";
import { Welcome } from "src/components/Welcome/Welcome";
import { useChat } from "src/contexts/chat.context";
import { dictionary, i18n } from "src/utils";
import { v4 } from "uuid";
import SparkleIcon from "../../assets/images/sparkle-icon.svg";
import { type AntIconName, DynamicIcon } from "./admin/tabs/prompt-bank";
import AiAgentApi from "./api";
import { AiAgentChatType, ChatOption } from "./enums";
import PromptsModal from "./prompts-modal";
import { componentStyle, fullWidth, gradientBackground, gradientBackgroundWelcome } from "./styles";
import AiAgentTitleTag from "./title-tag";
import {
  type AiAgentCompanyPrompt,
  type AiAgentConversation,
  type AiAgentFreeRecommendedPrompts,
  type AiAgentInteraction,
  AiAgentInteractionRole,
} from "./types";

const { TextArea } = Input;
const { Text } = Typography;

const md = markdownit({ html: true, breaks: true });

const CONVERSATION_TOKEN_LIMIT = process.env.REACT_APP_AI_CONVERSATION_TOKEN_LIMIT
  ? parseInt(process.env.REACT_APP_AI_CONVERSATION_TOKEN_LIMIT)
  : 250_000;

interface SelectedFile {
  file: File;
  error?: string;
}

const MAX_SIZE_MB = 10;

interface ChatProps {
  userId?: string;
  token: string;
  isAdmin: boolean;
  companyCode?: string;
  group: "A" | "B" | null;
  loadInitialPrompts: boolean;
  setLoadInitialPrompts: React.Dispatch<React.SetStateAction<boolean>>;
  isFreeTrial?: boolean;
  userName?: string;
  email?: string;
  companyCnpjCpf?: string;
  companyName?: string;
  managerUserPhone?: string;
  closeChat: () => void;
  lite?: boolean;
}

export default function Chat({
  userId,
  token,
  isAdmin,
  companyCode,
  group,
  loadInitialPrompts,
  setLoadInitialPrompts,
  isFreeTrial,
  userName,
  email,
  companyCnpjCpf,
  companyName,
  managerUserPhone,
  closeChat,
  lite,
}: ChatProps) {
  const socketRef = useRef<Socket | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { option, setOption } = useChat();

  const [sending, setSending] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [results, setResults] = useState<AiAgentInteraction[]>([]);
  const [finished, setFinished] = useState(true);
  const [runningTools, setRunningTools] = useState(false);
  const [usage, setUsage] = useState(0);
  const [replying, setReplying] = useState<string | null>(null);
  const [companyPrompts, setCompanyPrompts] = useState<AiAgentCompanyPrompt[]>([]);
  const [promptsModal, setPromptsModal] = useState<"favorites" | "recommendations" | null>(null);
  const [recommendedPrompts, setRecommendedPrompts] = useState<AiAgentFreeRecommendedPrompts>({
    prompts: [],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [chatType, setChatType] = useState(AiAgentChatType.AgenteQR);
  const [transferredToHuman, setTransferredToHuman] = useState(false);
  const [chatClosed, setChatClosed] = useState(false);

  async function getFreeRecommendedPrompts() {
    new AiAgentApi(token).getFreeRecommendedPrompts().then(setRecommendedPrompts);
  }

  useEffect(() => {
    if (token)
      new AiAgentApi(token).getCompanyPrompts().then((data) => {
        setCompanyPrompts(data);
      });

    const socket = connectSocket();

    return () => {
      socket.off();
      socket.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (option !== ChatOption.CHAT) return;
    if (textAreaRef.current) textAreaRef.current.focus();
    if (!sending && finished) getConversation();
  }, [option]);

  function getConversation() {
    const conversation = localStorage.getItem("current-conversation");
    if (!conversation) {
      setConversationId(undefined);
      setResults([]);
      getFreeRecommendedPrompts();
      return;
    }
    const parsedConversation: AiAgentConversation = JSON.parse(conversation);
    const { createdAt } = parsedConversation;
    if (dayjs().diff(dayjs(createdAt), "hours") < 24) {
      setConversationId(parsedConversation.id);
      setResults(
        parsedConversation.messages.map(({ id, role, content, feedback, processedAt, avatarUrl }) => ({
          id,
          role,
          content,
          feedback,
          processedAt,
          avatarUrl,
        })),
      );
      setUsage(parsedConversation.usage ?? 0);
    }
    setChatType(parsedConversation.type ?? 1);
    if (parsedConversation.type === AiAgentChatType.LiveChat) {
      setChatClosed(true);
    } else {
      setChatClosed(false);
    }
  }

  function connectSocket() {
    const socket = io(process.env.REACT_APP_FACILE_ANA_AGENT as string, {
      auth: { token },
    });

    socketRef.current = socket;
    return socket;
  }

  function reconnectSocket() {
    if (socketRef.current) {
      socketRef.current.off();
      socketRef.current.disconnect();
    }

    connectSocket();

    clearIntervals();
    setFinished(true);
    setSending(null);
    setReplying(null);
    setRunningTools(false);
    setResults((prev) => prev.slice(0, -2));
  }

  function clearIntervals() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  async function send(fixedInput?: string) {
    const finalInput = fixedInput ?? input;

    if (!finalInput || !!sending || !finished) return;

    const replyId = v4();
    const eventId = v4();

    let updatedResults: AiAgentInteraction[] = [];

    if (chatType !== AiAgentChatType.LiveChat) {
      setFinished(false);
      setSending(replyId);
      setReplying(replyId);
    }

    const userMessageProcessedAt = new Date().toISOString();

    setResults((prevResults) => {
      const updatedResults = [
        ...prevResults,
        { id: v4(), content: finalInput, role: AiAgentInteractionRole.User, processedAt: userMessageProcessedAt },
        ...(chatType !== AiAgentChatType.LiveChat
          ? [{ id: replyId, content: "", role: AiAgentInteractionRole.Assistant }]
          : []),
      ];

      //se for chat ao vivo já salva logo a mensagem porque a resposta não é aguardada, ou seja, o usuário pode enviar
      //várias mensagens em seguida antes do atendente humano responder
      if (chatType === AiAgentChatType.LiveChat) {
        const currentConversation = localStorage.getItem("current-conversation");
        let conversationData: AiAgentConversation | null = null;
        if (currentConversation) conversationData = JSON.parse(currentConversation);
        localStorage.setItem(
          "current-conversation",
          JSON.stringify({
            id: conversationData?.id ?? conversationId,
            createdAt: conversationData?.createdAt ?? dayjs().toISOString(),
            messages: updatedResults.map(({ id, role, content, processedAt, avatarUrl }) => ({
              id,
              role,
              content,
              processedAt,
              avatarUrl,
            })),
            type: chatType,
          }),
        );
      }

      return updatedResults;
    });

    setFiles([]);
    setInput("");

    // Se houver arquivo, envia via FormData para o backend
    if (files.length) {
      const validFiles = files.filter((f) => !f.error).map((f) => f.file);

      if (validFiles.length === 0) {
        alert("Nenhum arquivo válido para envio.");
        return;
      }
      const formData = new FormData();
      formData.append("message", finalInput);
      formData.append("threadId", conversationId ?? "");
      formData.append("email", email || "");
      formData.append("name", userName || "");
      formData.append("companyCode", companyCode || "");
      validFiles.forEach((file) => {
        formData.append("file[]", file);
      });

      try {
        await await new AiAgentApi(token).uploadLiveChatFile(formData);
      } catch (err) {
        console.error("Erro ao enviar mensagem com anexo:", err);
      }
    } else {
      // Se não houver arquivo, mantém a lógica atual via socket
      socketRef?.current?.emit("conversation/receive", {
        eventId,
        input: finalInput,
        conversationId,
        assistantMessageId: replyId,
        userMessageProcessedAt,
        chatType,
        userName,
        userEmail: email,
        userPhone: managerUserPhone,
        companyName,
        companyCnpjCpf,
        isFreeTrial,
        transferredToHuman,
        lite,
      });

      clearIntervals();

      const messages = [
        i18n("aiAgent.loadingMessages.1"),
        i18n("aiAgent.loadingMessages.2"),
        i18n("aiAgent.loadingMessages.3"),
        i18n("aiAgent.loadingMessages.4"),
        i18n("aiAgent.loadingMessages.5"),
      ];
      let index = 0;

      if (chatType !== AiAgentChatType.LiveChat) {
        timeoutRef.current = setTimeout(() => {
          intervalRef.current = setInterval(() => {
            setSending(messages[index]);
            index = (index + 1) % messages.length;
          }, 2000);
        }, 2000);
      }

      socketRef?.current?.on(eventId, async (response) => {
        setRunningTools(false);

        if (response?.error) {
          clearIntervals();
          if (response?.errorCode === 0 || response?.errorCode === -2 || response?.errorCode === -3) {
            setChatType(AiAgentChatType.LiveChat);
          }
          setFinished(true);
          setSending(null);
          setReplying(null);
          setResults((prevResults) =>
            prevResults.map((element) =>
              element.id === replyId
                ? {
                    ...element,
                    content: element.content.concat("\n" + response.error),
                    isError: true,
                    processedAt: response.assistantMessageProcessedAt,
                  }
                : element,
            ),
          );
          setConversationId(response.conversationId);
          const currentConversation = localStorage.getItem("current-conversation");
          let conversationData: AiAgentConversation | null = null;
          if (currentConversation) conversationData = JSON.parse(currentConversation);
          localStorage.setItem(
            "current-conversation",
            JSON.stringify({
              id: conversationData?.id ?? response.conversationId,
              createdAt: conversationData?.createdAt ?? dayjs().toISOString(),
              messages: updatedResults.map(({ id, role, content, processedAt, avatarUrl }) => ({
                id,
                role,
                content,
                processedAt,
                avatarUrl,
              })),
              type: response?.chatType,
            }),
          );
        }

        if (response?.done) {
          clearIntervals();
          setFinished(true);
          setSending(null);
          setReplying(null);
          setConversationId(response.conversationId);
          const currentConversation = localStorage.getItem("current-conversation");
          let conversationData: AiAgentConversation | null = null;
          if (currentConversation) conversationData = JSON.parse(currentConversation);
          updatedResults[updatedResults.length - 1].processedAt = response.assistantMessageProcessedAt;
          updatedResults[updatedResults.length - 2].processedAt = userMessageProcessedAt;
          localStorage.setItem(
            "current-conversation",
            JSON.stringify({
              id: conversationData?.id ?? response.conversationId,
              createdAt: conversationData?.createdAt ?? dayjs().toISOString(),
              messages: updatedResults.map(({ id, role, content, processedAt, avatarUrl }) => ({
                id,
                role,
                content,
                processedAt,
                avatarUrl,
              })),
              type: response?.chatType,
            }),
          );
          setUsage(response?.usage ?? 0);
          setLoadInitialPrompts(false);
        }

        if (response?.delta) {
          clearIntervals();
          setResults((prevResults) => {
            updatedResults = prevResults.map((element) =>
              element.id === replyId ? { ...element, content: element.content.concat(response.delta) } : element,
            );
            if (!prevResults.find((element) => element.id === replyId)) {
              updatedResults.push({
                id: v4(),
                role: AiAgentInteractionRole.Assistant,
                content: response.delta,
                processedAt: new Date().toISOString(),
                avatarUrl: undefined,
              });
            }
            return updatedResults;
          });

          if (response?.transferredToHuman) {
            setChatType(AiAgentChatType.LiveChat);
            setTransferredToHuman(true);
            if (response?.closedConversation) {
              setChatClosed(true);
            }
          }

          setSending(null);
        } else if (response?.customerServiceMessage) {
          clearIntervals();
          setFinished(true);
          setSending(null);
          setReplying(null);

          const message = response.customerServiceMessage;
          if (response.replyId === replying) {
            const replyId = response?.replyId;
            setResults((prevResults) => {
              updatedResults = prevResults.map((element) =>
                element.id === replyId
                  ? { ...element, content: message, role: response.role, avatarUrl: response.avatarUrl }
                  : element,
              );
              updatedResults[updatedResults.length - 1].processedAt = response.assistantMessageProcessedAt;
              updatedResults[updatedResults.length - 1].avatarUrl = response.avatarUrl;
              updatedResults[updatedResults.length - 1].role = response.role;
              updatedResults[updatedResults.length - 2].processedAt = userMessageProcessedAt;
              const currentConversation = localStorage.getItem("current-conversation");
              let conversationData: AiAgentConversation | null = null;
              if (currentConversation) conversationData = JSON.parse(currentConversation);
              localStorage.setItem(
                "current-conversation",
                JSON.stringify({
                  id: conversationData?.id ?? response.conversationId,
                  createdAt: conversationData?.createdAt ?? dayjs().toISOString(),
                  messages: updatedResults.map(({ id, role, content, processedAt, avatarUrl }) => ({
                    id,
                    role,
                    content,
                    processedAt,
                    avatarUrl,
                  })),
                  type: response?.chatType,
                }),
              );
              return updatedResults;
            });
          } else {
            setResults((prevResults) => {
              updatedResults = [
                ...prevResults,
                {
                  id: v4(),
                  role: response.role,
                  content: response.customerServiceMessage,
                  processedAt: new Date().toISOString(),
                  avatarUrl: response.avatarUrl,
                },
              ];
              updatedResults[updatedResults.length - 1].processedAt = response.assistantMessageProcessedAt;
              const currentConversation = localStorage.getItem("current-conversation");
              let conversationData: AiAgentConversation | null = null;
              if (currentConversation) conversationData = JSON.parse(currentConversation);
              localStorage.setItem(
                "current-conversation",
                JSON.stringify({
                  id: conversationData?.id ?? response.conversationId,
                  createdAt: conversationData?.createdAt ?? dayjs().toISOString(),
                  messages: updatedResults.map(({ id, role, content, processedAt, avatarUrl }) => ({
                    id,
                    role,
                    content,
                    processedAt,
                    avatarUrl,
                  })),
                  type: response?.chatType,
                }),
              );
              return updatedResults;
            });
          }

          if (response?.closedConversation) {
            setChatClosed(true);
          }

          setConversationId(response.conversationId);

          setUsage(response?.usage ?? 0);
          setLoadInitialPrompts(false);
        } else if (response?.runningTools) {
          setRunningTools(true);
          setResults((prevResults) => {
            updatedResults = prevResults.map((element) =>
              element.id === replyId ? { ...element, content: element.content.concat("\n\n") } : element,
            );
            return updatedResults;
          });
        } else if (response?.transferringToHuman) {
          clearIntervals();
          setSending(null);
          setReplying(null);

          setResults((prevResults) => {
            updatedResults = prevResults.map((element) =>
              element.id === replyId
                ? {
                    ...element,
                    content: element.content.concat(
                      "Sua conversa está sendo transferida para um atendente humano, por favor aguarde...",
                    ),
                  }
                : element,
            );
            return updatedResults;
          });
        }
      });
    }
  }

  const lastMessage = useMemo(() => {
    return results.length ? results[results.length - 1] : null;
  }, [results]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [option, lastMessage, runningTools]);

  function newConversation() {
    getFreeRecommendedPrompts();
    setResults([]);
    setUsage(0);
    setConversationId(undefined);
    localStorage.removeItem("current-conversation");
    setChatType(AiAgentChatType.AgenteQR);
    setChatClosed(false);
  }

  async function sendMessageFeedback(id: string, feedback: boolean) {
    const sendMessageFeedbackResult = await new AiAgentApi(token).sendMessageFeedback(id, feedback);
    if (sendMessageFeedbackResult) {
      const updatedResults = results.map((element) => (element.id === id ? { ...element, feedback } : element));
      setResults(updatedResults);
      const currentConversation = localStorage.getItem("current-conversation");
      let conversationData: AiAgentConversation | null = null;
      if (currentConversation) conversationData = JSON.parse(currentConversation);
      if (conversationData) {
        localStorage.setItem(
          "current-conversation",
          JSON.stringify({
            id: conversationData.id,
            createdAt: conversationData?.createdAt || dayjs().toISOString(),
            messages: updatedResults.map(({ id, role, content, feedback, processedAt, avatarUrl }) => ({
              id,
              role,
              content,
              feedback,
              processedAt,
              avatarUrl,
            })),
            type: chatType,
          }),
        );
      }
    }
  }

  async function upsertCompanyPrompt(content: string) {
    const finalContent = content.trim();
    const existingCompanyPrompt = companyPrompts.find((cp) => cp.content.toLowerCase() === finalContent.toLowerCase());
    const tempId = v4();
    if (!existingCompanyPrompt) {
      setCompanyPrompts((prev: any) => [
        ...prev,
        {
          id: tempId,
          companyCode,
          content: finalContent,
          active: true,
          createdAt: new Date().toISOString(),
        },
      ]);
    } else {
      setCompanyPrompts((prev) =>
        prev.map((el) => (el.id === existingCompanyPrompt.id ? { ...el, active: !el.active } : el)),
      );
    }
    const companyPrompt = await new AiAgentApi(token).upsertCompanyPrompt(content);
    if (!companyPrompt) {
      if (!existingCompanyPrompt) {
        setCompanyPrompts((prev) => prev.filter(({ id }) => id !== tempId));
      } else {
        setCompanyPrompts((prev) =>
          prev.map((el) => (el.id === existingCompanyPrompt.id ? { ...el, active: !el.active } : el)),
        );
      }
    } else {
      if (!existingCompanyPrompt)
        setCompanyPrompts((prev) => prev.map((el) => (el.id === tempId ? companyPrompt : el)));
    }
  }

  const month = dayjs().get("month");
  const year = dayjs().get("year");

  const prompts = {
    A: [
      {
        key: "1",
        label: i18n("aiAgent.welcome.prompts.A.1"),
        icon: "CloudOutlined",
      },
      {
        key: "2",
        label: i18n("aiAgent.welcome.prompts.A.2"),
        icon: "CloudOutlined",
      },
      {
        key: "3",
        label: i18n("aiAgent.welcome.prompts.A.3") + i18n(`months.${month}`) + " " + i18n("of") + " " + year + "?",
        icon: "CloudOutlined",
      },
    ],
    B: [
      {
        key: "1",
        label: i18n("aiAgent.welcome.prompts.B.1") + i18n(`months.${month}`) + " " + i18n("of") + " " + year + "?",
        icon: "CloudOutlined",
      },
      {
        key: "2",
        label: i18n("aiAgent.welcome.prompts.B.2") + i18n(`months.${month}`) + " " + i18n("of") + " " + year + ".",
        icon: "FilePdfOutlined",
      },
      {
        key: "3",
        label: i18n("aiAgent.welcome.prompts.B.3"),
        icon: "CloudOutlined",
      },
    ],
    freeTrial: [
      {
        key: "1",
        label: i18n("aiAgent.welcome.prompts.freeTrial.1"),
        icon: "ControlOutlined",
      },
      {
        key: "2",
        label: i18n("aiAgent.welcome.prompts.freeTrial.2"),
        icon: "ControlOutlined",
      },
      {
        key: "3",
        label: i18n("aiAgent.welcome.prompts.freeTrial.3"),
        icon: "ControlOutlined",
      },
    ],
  };

  const [files, setFiles] = useState<SelectedFile[]>([]);
  const validateFile = (file: File): string | undefined => {
    if (!file.type.startsWith("image/") && !file.type.startsWith("audio/") && file.type !== "application/pdf") {
      return `Tipo inválido: ${file.type}`;
    }

    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_SIZE_MB) {
      return `Arquivo maior que ${MAX_SIZE_MB}MB`;
    }

    return undefined;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files).map((file) => ({
      file,
      error: validateFile(file),
    }));

    setFiles(selected);

    const fileNames = selected.map((f) => f.file.name).join(", ");
    const anexosTag = `[Anexos: ${fileNames}]`;

    if (input.includes("[Anexos:")) {
      const updated = input.replace(/\[Anexos:[^\]]*\]/g, anexosTag);
      setInput(updated);
    } else {
      setInput((prev) => `${prev}\n${anexosTag}`);
    }
  };

  const removeFile = (fileName: string) => {
    setFiles((prev) => prev.filter((f) => f.file.name !== fileName));

    const anexosTagRegex = /\[Anexos:[^\]]*\]/g;
    const currentTag = input.match(anexosTagRegex)?.[0];
    if (currentTag) {
      const fileNames = files
        .filter((f) => f.file.name !== fileName)
        .map((f) => f.file.name)
        .join(", ");
      const newTag = fileNames ? `[Anexos: ${fileNames}]` : "";
      const updated = input.replace(anexosTagRegex, newTag);
      setInput(updated.trim());
    }
  };

  function getRandomHSL(index: number) {
    const h = Math.floor((1 / index) * 360);
    const s = 70;
    const l = 50;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }

  return (
    <Flex
      style={{
        ...componentStyle,
        width: "100%",
        height: "100%",
        display: option !== ChatOption.CHAT ? "none" : undefined,
        position: "relative",
      }}
      vertical
      gap={16}
    >
      <PromptsModal
        open={promptsModal}
        cancel={() => {
          setPromptsModal(null);
          textAreaRef?.current?.focus();
        }}
        favoritePrompts={companyPrompts.filter(({ active }) => active).map(({ id, content }) => ({ id, content }))}
        setInput={setInput}
        token={token}
        isAdmin={isAdmin}
      />
      <Flex
        style={fullWidth}
        justify="flex-start"
      >
        <Flex
          style={fullWidth}
          justify="space-between"
          align="center"
        >
          <AiAgentTitleTag
            name={
              chatClosed
                ? i18n(dictionary.label.history)
                : chatType === AiAgentChatType.LiveChat
                ? i18n("aiAgent.liveChat")
                : i18n("qrAgent")
            }
          />
          <Flex gap={8}>
            <Popconfirm
              title={i18n("aiAgent.newConversation.confirmation")}
              okText={i18n(dictionary.label.yes)}
              cancelText={i18n(dictionary.label.no)}
              onConfirm={newConversation}
            >
              <Button
                shape="round"
                size="small"
                icon={<PlusOutlined />}
                disabled={!results.length || !!sending || !finished || runningTools}
              >
                {i18n("aiAgent.newConversation.button")}
              </Button>
            </Popconfirm>
            <Button
              shape="circle"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => {
                localStorage.setItem("ai_chat_option", ChatOption.HISTORY.toString());
                setOption(ChatOption.HISTORY);
              }}
              disabled={!!sending || !finished || runningTools}
            />
            <Button
              shape="circle"
              size="small"
              icon={<CloseOutlined />}
              onClick={closeChat}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex
        style={fullWidth}
        vertical
        gap={8}
      >
        <Flex
          style={{ ...fullWidth, height: "calc(100vh - 280px)", padding: "0 16px", overflowY: "auto" }}
          ref={chatRef}
          vertical
          gap={16}
        >
          {!!results?.length && (
            <>
              {results.map(({ id, content, role, feedback, isError, processedAt, avatarUrl }, index) => {
                const settings = {
                  placement: "end",
                  avatar: {
                    icon: <UserOutlined />,
                    src: `/perfil/${userId}`,
                  },
                };
                if (role === AiAgentInteractionRole.Assistant || role === AiAgentInteractionRole.Developer) {
                  settings.placement = "start";
                  settings.avatar = {
                    icon: role === AiAgentInteractionRole.Assistant ? <RobotOutlined /> : <UserOutlined />,
                    src: avatarUrl ?? "",
                  };
                }
                return (
                  <Bubble
                    key={index}
                    content={
                      !!sending && index === results.length - 1 ? (
                        <Flex
                          align="center"
                          gap={6}
                        >
                          <span>{sending}...</span>
                          <LoadingOutlined style={{ color: "#884EA6" }} />
                        </Flex>
                      ) : (
                        <>
                          <Typography>
                            {
                              <div
                                className="markdown-content"
                                dangerouslySetInnerHTML={{ __html: md.render(content) }}
                              />
                            }
                            {runningTools && index === results.length - 1 && (
                              <LoadingOutlined style={{ color: "#884EA6", marginTop: 16, fontSize: "large" }} />
                            )}
                          </Typography>
                          {!!processedAt && (
                            <Text
                              style={{ fontSize: "smaller" }}
                              type="secondary"
                            >
                              {dayjs(processedAt).format(i18n("fullDateFormat"))}
                            </Text>
                          )}
                        </>
                      )
                    }
                    placement={settings.placement as "start" | "end"}
                    avatar={settings.avatar}
                    shape="round"
                    loading={id === sending}
                    styles={{ footer: { marginTop: 6 } }}
                    footer={
                      role === "user" && chatType !== AiAgentChatType.LiveChat ? (
                        <Flex>
                          <Rate
                            count={1}
                            value={
                              companyPrompts.find(
                                (companyPrompt) =>
                                  companyPrompt.content.toLowerCase() === content.toLowerCase() && companyPrompt.active,
                              )
                                ? 1
                                : 0
                            }
                            onChange={async () => {
                              await upsertCompanyPrompt(content);
                            }}
                          />
                        </Flex>
                      ) : role === AiAgentInteractionRole.Assistant &&
                        !(id === sending) &&
                        replying !== id &&
                        !isError &&
                        chatType !== AiAgentChatType.LiveChat ? (
                        <Flex gap={2}>
                          <Button
                            size="small"
                            type="text"
                            icon={<LikeOutlined />}
                            style={
                              feedback
                                ? {
                                    color: "#389E0D",
                                    backgroundColor: "#E6F4EA",
                                  }
                                : {}
                            }
                            onClick={() => {
                              if (feedback !== true) sendMessageFeedback(id, true);
                            }}
                            disabled={isAdmin}
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<DislikeOutlined />}
                            style={
                              feedback === false
                                ? {
                                    color: "#F5222D",
                                    backgroundColor: "#FFF1F0",
                                  }
                                : {}
                            }
                            onClick={() => {
                              if (feedback !== false) sendMessageFeedback(id, false);
                            }}
                            disabled={isAdmin}
                          />
                        </Flex>
                      ) : (
                        role === AiAgentInteractionRole.Assistant &&
                        replying &&
                        !isError &&
                        chatType !== AiAgentChatType.LiveChat && (
                          <Button
                            size="small"
                            type="text"
                            icon={<PauseCircleOutlined />}
                            onClick={reconnectSocket}
                          />
                        )
                      )
                    }
                  />
                );
              })}
            </>
          )}
        </Flex>
        {/* Prompts recomendados */}
        {!results?.length && !loadInitialPrompts && (
          <Flex
            style={{ ...fullWidth, maxHeight: "calc(100vh - 280px)", overflowY: "auto", overflowX: "hidden" }}
            vertical
            gap={8}
          >
            <AiAgentWelcome />
            <Flex vertical>
              <Flex
                style={{ marginTop: 12, flexWrap: "wrap" }}
                gap={8}
              >
                {recommendedPrompts.prompts.map(({ id, content, title, icon }, index) => (
                  <Flex
                    key={id}
                    className="custom-prompt"
                    style={{
                      height: 48,
                      background: "#FFFFFF",
                      border: "1px solid #F0F0F0",
                      padding: "12px 16px",
                      borderRadius: 8,
                      cursor: "pointer",
                      userSelect: "none",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onClick={() => {
                      setInput(content);
                    }}
                  >
                    {icon && (
                      <DynamicIcon
                        iconName={icon as AntIconName}
                        style={{ color: getRandomHSL(index) }}
                      />
                    )}
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{title}</span>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Flex>
        )}
        {/* Prompts iniciais (usuários que nunca interagiram com o agente) */}
        {!results.length && loadInitialPrompts && group && (
          <Flex
            vertical
            gap={8}
          >
            <AiAgentWelcome />
            <Flex
              style={{ ...gradientBackground, padding: 16, borderRadius: 8 }}
              vertical
            >
              <Flex
                style={{ marginTop: 12, flexWrap: "wrap" }}
                vertical
                gap={8}
              >
                {prompts[isFreeTrial ? "freeTrial" : group].map(({ key, label, icon }, index) => (
                  <Flex
                    key={key}
                    className="custom-prompt"
                    style={{
                      height: 48,
                      background: "#FFFFFF",
                      border: "1px solid #F0F0F0",
                      padding: "12px 16px",
                      borderRadius: 8,
                      cursor: "pointer",
                      userSelect: "none",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onClick={async () => {
                      await send(label);
                    }}
                  >
                    {icon && (
                      <DynamicIcon
                        iconName={icon as AntIconName}
                        style={{ color: getRandomHSL(index) }}
                      />
                    )}
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Flex>
        )}
        {!results?.length && (
          <div
            style={{
              marginTop: 24,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              marginBottom: 32,
            }}
          >
            <img
              src={SparkleIcon}
              style={{ width: 48, height: 48 }}
            />
            <span style={{ color: "#722ED1", fontWeight: 500, fontSize: 14 }}>{i18n("aiAgent.welcome.intro")}</span>
          </div>
        )}
        {usage < CONVERSATION_TOKEN_LIMIT ? (
          <>
            <Flex
              className="custom-text-area-container"
              style={{
                ...fullWidth,
                border: "1px solid #E0DFFF",
                borderRadius: 16,
                boxShadow: document.activeElement === textAreaRef.current ? "0 0 5px #837EFF4D" : "none",
                cursor: "text",
              }}
              vertical
              onClick={() => {
                if (textAreaRef.current) textAreaRef?.current?.focus();
              }}
            >
              <TextArea
                className="custom-text-area-field"
                style={{
                  border: "none",
                  borderRadius: 16,
                  outline: "none",
                  boxShadow: "none",
                }}
                ref={textAreaRef}
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder={i18n("aiAgent.placeholder")}
                value={input}
                onChange={({ target }) => {
                  setInput(target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                maxLength={250}
                readOnly={isAdmin}
                disabled={chatClosed}
              />
              <ul>
                {files.map((f, idx) => (
                  <li
                    key={idx}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    📎 {f.file.name}{" "}
                    {f.error ? (
                      <>
                        <span style={{ color: "red" }}>{f.error}</span>
                        <Button
                          type="text"
                          size="small"
                          onClick={() => {
                            removeFile(f.file.name);
                          }}
                          style={{ color: "red" }}
                        >
                          ❌
                        </Button>
                      </>
                    ) : (
                      "✔️"
                    )}
                  </li>
                ))}
              </ul>

              <Flex
                style={{ ...fullWidth, marginBottom: 8 }}
                justify="flex-end"
                gap={2}
              >
                {chatType === AiAgentChatType.LiveChat ? (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <Button
                      shape="circle"
                      type="text"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      disabled={!!sending || !finished || chatClosed}
                    >
                      <PaperClipOutlined
                        style={{ color: !!sending || !finished || chatClosed ? undefined : "#884EA6" }}
                      />
                    </Button>
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept="image/*,audio/*,application/pdf"
                      onChange={handleFileChange}
                    />
                    <Button
                      shape="circle"
                      type="text"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowEmojiPicker((prev) => !prev);
                      }}
                      disabled={!!sending || !finished || chatClosed}
                    >
                      <SmileOutlined
                        style={{
                          color: !!sending || !finished || chatClosed ? undefined : "#884EA6",
                        }}
                      />
                    </Button>

                    {showEmojiPicker && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "100%",
                          right: 0,
                          marginBottom: "8px",
                          zIndex: 1000,
                        }}
                      >
                        <Picker
                          data={data}
                          onEmojiSelect={(emoji: any) => {
                            setInput((prev: string) => prev + emoji.native);
                            setShowEmojiPicker(false);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Dropdown
                      trigger={["click"]}
                      placement="top"
                      arrow
                      menu={{
                        onClick: ({ key, domEvent }) => {
                          domEvent.stopPropagation();
                          setPromptsModal(key as "favorites" | "recommendations");
                        },
                        items: [
                          {
                            icon: <StarOutlined />,
                            key: "favorites",
                            label: i18n("aiAgent.prompts.favorites"),
                          },
                          {
                            icon: <BulbOutlined />,
                            key: "recommendations",
                            label: i18n("aiAgent.prompts.recommendations"),
                          },
                        ],
                      }}
                    >
                      <Button
                        shape="circle"
                        type="text"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <ThunderboltOutlined style={{ color: "#884EA6" }} />
                      </Button>
                    </Dropdown>
                  </>
                )}
                <Button
                  style={{ marginRight: 8 }}
                  shape="circle"
                  type="text"
                  size="small"
                  disabled={!input || !!sending || !finished || runningTools || chatClosed}
                  onClick={(event) => {
                    event.stopPropagation();
                    send();
                  }}
                >
                  <SendOutlined
                    style={{ color: !input || !!sending || !finished || runningTools ? undefined : "#884EA6" }}
                  />
                </Button>
              </Flex>
            </Flex>
          </>
        ) : (
          <>
            <Divider style={{ margin: 8 }} />
            <Flex
              style={{ textAlign: "center", padding: "0 32px" }}
              justify="center"
            >
              <Text type="danger">
                {i18n("aiAgent.reachedLimit.1")}&nbsp;
                <span
                  style={{ fontWeight: 600, cursor: "pointer" }}
                  onClick={newConversation}
                >
                  {i18n("aiAgent.reachedLimit.2")}
                </span>
                &nbsp;{i18n("aiAgent.reachedLimit.3")}
              </Text>
            </Flex>
            <Divider style={{ margin: 8 }} />
          </>
        )}
      </Flex>
      <Flex
        justify="center"
        style={{
          position: "absolute",
          bottom: -8,
          left: 0,
          right: 0,
        }}
      >
        <Text
          style={{ fontSize: "small" }}
          type={chatClosed ? "danger" : chatType === AiAgentChatType.LiveChat ? "success" : "secondary"}
        >
          <ExclamationCircleOutlined />
          &nbsp;
          {chatClosed
            ? i18n("aiAgent.liveChatDone")
            : chatType === AiAgentChatType.LiveChat
            ? i18n("aiAgent.liveChatInfo")
            : i18n("aiAgent.betaWarning")}
        </Text>
      </Flex>
    </Flex>
  );
}

function AiAgentWelcome() {
  return (
    <Welcome
      style={gradientBackgroundWelcome}
      icon="https://mdn.alipayobjects.com/huamei_iwk9zp/afts/img/A*s5sNRo5LjfQAAAAAAAAAAAAADgCCAQ/fmt.webp"
      title={i18n("aiAgent.welcome.title")}
      description={i18n("aiAgent.welcome.description")}
    />
  );
}
